import React from 'react'
import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import { ConnectedDotsIcon } from '../../components/Icons/ConnectedDotsIcon'
import { TransitionButton } from '../../components/Buttons/TransitionButton'
import VerificationCodeInput from './VerificationCodeInput'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { validatePassword } from '../../services/formhelper.service'

type ForgotPasswordComponentProps = {
  emailNewCode?: () => void
  onSubmit: (data: any) => void
}

type State = {
  code: string
  password: string
  showPassword: boolean
  validationErrors: Record<string, string>
}

const validateCode = (code: string) => !/^\d{6}$/.test(code)

const ChangePasswordComponent: React.FC<ForgotPasswordComponentProps> = ({
  emailNewCode,
  onSubmit
}): JSX.Element => {
  const theme = useTheme()
  const [values, setValues] = React.useState<State>({
    code: '',
    password: '',
    showPassword: false,
    validationErrors: {}
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    onSubmit({ code: values.code, password: values.password })
  }

  const validate = (prop: keyof State) => (value: string) => {
    let validationErrors = {}
    if (prop === 'password' && validatePassword(value)) {
      validationErrors = { password: 'error' }
    }
    if (prop === 'code' && validateCode(value)) {
      validationErrors = { code: 'error' }
    }
    return validationErrors
  }

  const handleChange = (prop: keyof State) => (value: string) => {
    setValues({
      ...values,
      [prop]: value,
      validationErrors: validate(prop)(value)
    })
  }

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword
    })
  }

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault()
  }

  const isDisabled =
    !values.code ||
    !values.password ||
    !!values.validationErrors.code ||
    !!values.validationErrors.password

  return (
    <div style={{ marginTop: theme.spacing(2) }}>
      <Typography
        component="h1"
        variant="h5"
        align="center"
        color="text.primary"
        gutterBottom
      >
        Password Recovery
      </Typography>

      <div style={{ textAlign: 'center' }}>
        <ConnectedDotsIcon sx={{ height: '1em', width: '2em' }} />
      </div>

      <Stack
        component="form"
        spacing={2}
        noValidate
        onSubmit={handleSubmit}
        sx={{
          p: theme.breakpoints.only('xs') ? 0 : 2
        }}
      >
        <Box>
          <Typography
            component="div"
            variant="body1"
            textAlign="left"
            style={{
              marginBottom: theme.spacing(2)
            }}
          >
            Check your inbox for a one-time code and enter it below along with
            your new account password.
            <br />
          </Typography>

          <VerificationCodeInput
            type="numeric"
            placeholder="000-000"
            onGetNewCode={emailNewCode}
            onChange={handleChange('code')}
          />

          <FormControl
            variant="outlined"
            required
            sx={{ minWidth: 300, width: '100%' }}
            error={!!values.password && !!values.validationErrors.password}
          >
            <InputLabel htmlFor="outlined-adornment-password">
              Password
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-password"
              type={values.showPassword ? 'text' : 'password'}
              value={values.password}
              onChange={(e) => handleChange('password')(e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {values.showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Password"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <FormHelperText id="password-helper-text">
                8 characters minimum
              </FormHelperText>
            </div>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center'
          }}
        >
          <TransitionButton
            text="Submit"
            type="submit"
            style={{ width: '8rem' }}
            disabled={isDisabled}
          />
        </Box>
      </Stack>
    </div>
  )
}

export default ChangePasswordComponent
